import { HttpClient } from '@angular/common/http';
import { inject, InjectionToken, Provider } from '@angular/core';
import { LaravelResult } from '@fsf/util';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  TrafficStation,
  trafficStationsResultSchema,
} from '../models/traffic-station.model';
import { TrafficStationRepository } from './traffic-station.repository';

/** Behind a token so the `@fsf/discover` lib takes no dependency on the app's `environment`. */
export const TRAFFIC_STATIONS_ENDPOINT = new InjectionToken<string>(
  'traffic-stations.endpoint.url',
);

export interface TrafficStationApiConfig {
  http: HttpClient;
  endpointUrl: string;
}

/**
 * Unwraps and Zod-parses before emitting, so a missing, renamed or wrong-typed field errors the
 * stream rather than producing a malformed layer.
 *
 * Not `@Injectable` — only ever built via `new`, so AOT must not analyze its config-object
 * constructor parameter as a DI token.
 */
export class TrafficStationApiRepository extends TrafficStationRepository {
  readonly #http: HttpClient;
  readonly #endpointUrl: string;

  constructor(config: TrafficStationApiConfig) {
    super();
    this.#http = config.http;
    this.#endpointUrl = config.endpointUrl;
  }

  getTrafficStations(): Observable<TrafficStation[]> {
    return this.#http
      .get<LaravelResult<unknown>>(this.#endpointUrl)
      .pipe(map((result) => trafficStationsResultSchema.parse(result.data)));
  }
}

/** `inject` runs inside the factory, which is an injection context. */
export function provideTrafficStationsApi(): Provider {
  return {
    provide: TrafficStationRepository,
    useFactory: () =>
      new TrafficStationApiRepository({
        http: inject(HttpClient),
        endpointUrl: inject(TRAFFIC_STATIONS_ENDPOINT),
      }),
  };
}
